import { create } from 'zustand';
import type { TenantConfig } from '../types/tenant';
import { getTenantConfig } from '../api/tenant';

interface TenantState {
  config: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  slug: string | null;

  loadConfig: (slug: string) => Promise<void>;
  clearError: () => void;
}

export const useTenantStore = create<TenantState>((set, get) => ({
  config: null,
  isLoading: true,
  error: null,
  slug: null,

  loadConfig: async (slug) => {
    // Skip if already loaded for this tenant
    if (get().slug === slug && get().config) {
      return;
    }

    set({ isLoading: true, error: null, slug });

    try {
      const config = await getTenantConfig(slug);
      set({ config, isLoading: false });

      // Apply tenant branding CSS variables
      const root = document.documentElement;
      const color = config.branding.primaryColor;

      // Generate color shades from primary color
      root.style.setProperty('--color-primary-500', color);
      root.style.setProperty('--color-primary-50', adjustColor(color, 0.95));
      root.style.setProperty('--color-primary-100', adjustColor(color, 0.9));
      root.style.setProperty('--color-primary-200', adjustColor(color, 0.75));
      root.style.setProperty('--color-primary-300', adjustColor(color, 0.6));
      root.style.setProperty('--color-primary-400', adjustColor(color, 0.3));
      root.style.setProperty('--color-primary-600', adjustColor(color, -0.1));
      root.style.setProperty('--color-primary-700', adjustColor(color, -0.2));
      root.style.setProperty('--color-primary-800', adjustColor(color, -0.3));
      root.style.setProperty('--color-primary-900', adjustColor(color, -0.4));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load tenant',
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Simple color adjustment utility
function adjustColor(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const adjust = (c: number) => {
    if (factor > 0) {
      // Lighten
      return Math.round(c + (255 - c) * factor);
    } else {
      // Darken
      return Math.round(c * (1 + factor));
    }
  };

  const toHex = (c: number) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0');

  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}
